import { callingWaitSeconds } from './callingWait'
import { secondsSince } from './clock'
import { pausedHeadline, requestKindLabel } from './deskStatus'
import { findActions } from './findActions'
import { formatMoment, t, type StaffKey, type StaffLanguage } from './staffText'
import type { CounterView, DeskStatus, RequestKind, Ticket, TicketState, WorkProfile } from './types'

/**
 * The floor board: every desk and every open visit in one place, for whoever may
 * act on other people's desks and customers.
 *
 * It offers nothing the server did not already allow. A supervisor could always
 * close a desk somebody left signed in (engine/counters.go, ownsDesk) and act on a
 * visit reception can find by number (lib/findActions); what was missing was one
 * screen that showed all of it without typing a number. The rules stay where they
 * are, and this file only reads them.
 */

export interface DeskRow {
  desk: CounterView
  /** How long the person at it has been signed in; 0 for a closed desk. */
  signedInSec: number
  /** How long the break it is on has lasted; 0 when it is not paused. */
  pausedSec: number
  offersSignOut: boolean
}

export function deskRows(counters: CounterView[], viewerId: string, canSupervise: boolean, now: number): DeskRow[] {
  return counters.map((desk) => {
    const staffed = desk.state !== 'closed' && Boolean(desk.staffId)
    return {
      desk,
      signedInSec: staffed ? secondsSince(desk.openedAt, now) : 0,
      pausedSec: desk.state === 'paused' ? secondsSince(desk.pausedSince, now) : 0,
      offersSignOut: canSupervise && staffed && desk.staffId !== viewerId,
    }
  })
}

type Profiles = readonly Pick<WorkProfile, 'id' | 'queueIds'>[]

/** The queues a desk calls: its work profile's. */
export function deskQueueIds(desk: Pick<CounterView, 'workProfileId'>, profiles: Profiles): string[] {
  return profiles.find((p) => p.id === desk.workProfileId)?.queueIds ?? []
}

/** Whether every open desk's queues are known, so "no open desk calls it" is a fact,
 *  not a profile list still loading or missing the desk's profile. */
export function knowsCallers(
  desks: readonly Pick<CounterView, 'state' | 'workProfileId'>[],
  profiles: Profiles,
): boolean {
  return desks.every((d) => d.state === 'closed' || profiles.some((p) => p.id === d.workProfileId))
}

/** The question before a sign-out, with the person and the customer in it. What
 *  it says about the customer is what releaseToQueue does: a called customer goes
 *  back to waiting, one being served or wrapped up is parked in the branch pool. */
export function signOutQuestion(desk: CounterView, lang: StaffLanguage): string {
  const question = desk.staffName
    ? t('floorBoard.signOutQuestion', lang, { who: desk.staffName, desk: desk.name })
    : t('floorBoard.signOutQuestionAnyone', lang, { desk: desk.name })
  if (!desk.displayNumber) return question
  const then = desk.ticketState === 'called' ? 'floorBoard.signOutCalled' : 'floorBoard.signOutAtDesk'
  return `${question} ${t(then, lang, { number: desk.displayNumber })}`
}

export type VisitAction = 'putBack' | 'remove' | 'move' | 'unpark'

/** Reception's rules for a found ticket, and Put back for a set-aside one — the
 *  counter's supervisor action (lib/putBack), offered here without the counter's
 *  "is it coming back to my desk anyway" condition, because the board is nobody's
 *  desk. */
export function visitActions(ticket: Pick<Ticket, 'state'>, canSupervise: boolean): VisitAction[] {
  const actions: VisitAction[] = findActions(ticket, canSupervise)
  if (ticket.state === 'parked' && canSupervise) actions.push('unpark')
  return actions
}

export type VisitGroup = '' | 'waiting' | 'desk' | 'parked' | 'missed'

const GROUP_STATES: Record<Exclude<VisitGroup, ''>, TicketState[]> = {
  waiting: ['waiting'],
  desk: ['called', 'serving', 'wrapup'],
  parked: ['parked'],
  missed: ['no_show'],
}

/** The filter's choices, in the order a visit moves through them, each with the
 *  key of its label. */
export const VISIT_GROUPS: Array<[VisitGroup, StaffKey]> = [
  ['', 'floorBoard.group.all'],
  ['waiting', 'floorBoard.group.waiting'],
  ['desk', 'floorBoard.group.desk'],
  ['parked', 'floorBoard.group.parked'],
  ['missed', 'floorBoard.group.missed'],
]

const OPEN: TicketState[] = ['waiting', 'called', 'serving', 'wrapup', 'parked', 'no_show']

/** The open visits, in the order they arrived — the order branchState keeps — and
 *  filtered. An empty queue id is every queue. */
export function visitRows(tickets: Ticket[], filter: { queueId: string; group: VisitGroup }): Ticket[] {
  return tickets.filter(
    (t) =>
      OPEN.includes(t.state) &&
      (!filter.queueId || t.queueId === filter.queueId) &&
      (!filter.group || GROUP_STATES[filter.group].includes(t.state)),
  )
}

/**
 * The time written beside a visit on the board, or nothing.
 *
 * The calling wait (lib/callingWait) only for a customer the server is still
 * waiting to call: in a line, or set aside in a pool. For a no-show it is a wait
 * that never stops — a ticket missed on Monday read "141h 01m" on Friday, as though
 * that customer were owed a call — so it says instead when the number was last
 * called, which is the counter's Missed card's figure and all the ticket knows
 * (no-show carries no time of its own). For a customer at a desk the question is
 * the desk's, and the Desks column answers it.
 */
export type VisitTime = { kind: 'wait'; seconds: number } | { kind: 'called'; seconds: number } | { kind: 'none' }

export function visitTime(ticket: Ticket, now: number): VisitTime {
  switch (ticket.state) {
    case 'waiting':
    case 'parked':
      return { kind: 'wait', seconds: callingWaitSeconds(ticket, now) }
    case 'no_show':
      return ticket.lastCalledAt ? { kind: 'called', seconds: secondsSince(ticket.lastCalledAt, now) } : { kind: 'none' }
    default:
      return { kind: 'none' }
  }
}

/** A sentence for the clerk whose desk closed under them, or '' when there is
 *  nothing to say. The floor view does not say who closed it, so the sentence names
 *  the two ways it happens. A change of branch is not a sign-out. */
export function signedOutElsewhere(
  before: CounterView | undefined,
  after: CounterView | undefined,
  viewerId: string,
  closingHere: boolean,
  lang: StaffLanguage,
  sameBranch = true,
): string {
  if (!sameBranch || closingHere || !before || before.state === 'closed' || before.staffId !== viewerId) return ''
  if (after && after.state !== 'closed' && after.staffId === viewerId) return ''
  return t('counter.signedOutElsewhere', lang, { desk: before.name })
}

/** The desk the Counter shows: the one this browser remembers when this person is
 *  signed in there, else the desk this person is signed in at — opened in another
 *  browser, or before somebody else used this one — else the remembered one, for
 *  "Open a desk" to preselect. Only ever this person's own: a desk somebody else
 *  holds is never joined. */
export function deskToShow(
  counters: readonly Pick<CounterView, 'counterId' | 'state' | 'staffId'>[],
  remembered: string,
  viewerId: string,
): string {
  const mine = (c: Pick<CounterView, 'state' | 'staffId'>) => c.state !== 'closed' && Boolean(viewerId) && c.staffId === viewerId
  if (counters.some((c) => c.counterId === remembered && mine(c))) return remembered
  return counters.find(mine)?.counterId ?? remembered
}

/** The desk Open a desk offers: the remembered one while free, else the only free one. */
export function deskToOffer(counters: readonly Pick<CounterView, 'counterId' | 'state'>[], remembered: string): string {
  const free = counters.filter((c) => c.state === 'closed')
  if (free.some((c) => c.counterId === remembered)) return remembered
  return free.length === 1 ? free[0]!.counterId : remembered
}

/** Where a visit came from, with the time it was issued, except for a booked visit:
 *  its booked time is already on the screen, and a second time beside it misleads. */
export function visitOrigin(ticket: Ticket, lang: StaffLanguage, zone?: string): string {
  const source = t(`counter.source.${ticket.source}`, lang)
  if (ticket.appointmentId || ticket.source === 'appointment') return source
  return `${source}, ${formatMoment(ticket.issuedAt, lang, { time: true, zone })}`
}

/**
 * The target drawn on a line's bar: the worst waiting customer's own; with nobody
 * waiting, the queue's override, else the tightest of its services — as
 * catalog.ServiceLevelFor picks.
 */
export function lineTarget(worstSec: number, queueSec: number | undefined, serviceSecs: number[]): number {
  if (worstSec > 0) return worstSec
  if (queueSec && queueSec > 0) return queueSec
  const set = serviceSecs.filter((s) => s > 0)
  return set.length ? Math.min(...set) : 0
}

/** A wait sized for a KPI tile: "38 min", "2 h 05 m", and only the hours from 10 h on. */
export function kpiWait(seconds: number, lang: StaffLanguage): string {
  const m = Math.max(0, Math.floor(seconds / 60))
  const h = Math.floor(m / 60)
  if (h === 0) return t('hall.min', lang, { n: m })
  if (h < 10) return t('floor.dash.hm', lang, { h, m: String(m % 60).padStart(2, '0') })
  return t('floor.dash.h', lang, { h })
}

/** A paused desk's words: Pending names what was asked for ("Lunch request"). */
export function pausedWords(status: DeskStatus | undefined, asked: RequestKind | undefined, lang: StaffLanguage): string {
  if (!status) return ''
  if (status === 'pending' && asked) return t('floor.dash.asked', lang, { kind: requestKindLabel(asked, lang) })
  return pausedHeadline(status, lang)
}

/** The reasons the Counter writes itself when it sets a ticket aside (pages/Counter.tsx
 *  OWN_REASONS): markers the chips already say, not a clerk's words. */
const COUNTER_REASONS = new Set(
  [
    { reason: 'waiting on the customer' },
    { reason: 'sent to a colleague' },
    { reason: 'sent to another desk' },
    { reason: 'transferred' },
    { reason: 'the desk was closed while this ticket was in service' },
  ].map((r) => r.reason),
)

/** Why a set-aside ticket is held, as the clerk typed it, or ''. */
export function holdReason(ticket: Pick<Ticket, 'state' | 'parkReason'>): string {
  const reason = ticket.parkReason?.trim() ?? ''
  return ticket.state === 'parked' && !COUNTER_REASONS.has(reason) ? reason : ''
}
